// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	ApplicationCommandOptionTypes,
	ApplicationCommandTypes,
	MAX_APPLICATION_COMMAND_BYTES,
	MAX_APPLICATION_COMMAND_CHOICE_NAME_LENGTH,
	MAX_APPLICATION_COMMAND_CHOICE_STRING_LENGTH,
	MAX_APPLICATION_COMMAND_CHOICES,
	MAX_APPLICATION_COMMAND_DESCRIPTION_LENGTH,
	MAX_APPLICATION_COMMAND_NAME_LENGTH,
	MAX_APPLICATION_COMMAND_OPTIONS,
	MAX_APPLICATION_COMMAND_SET_BYTES,
	MAX_APPLICATION_COMMAND_STRING_LENGTH,
	MAX_APPLICATION_COMMANDS_PER_SCOPE,
} from '@fluxer/constants/src/ApplicationCommandConstants';
import {ALL_PERMISSIONS} from '@fluxer/constants/src/ChannelConstants';
import {
	SnowflakeStringType,
	SnowflakeType,
	UnsignedInt64StringType,
} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

function codePointString(min: number, max: number) {
	return z.string().refine((value) => {
		const length = Array.from(value).length;
		return length >= min && length <= max;
	}, `Expected ${min}–${max} Unicode code points`);
}

export const ApplicationCommandName = z
	.string()
	.normalize('NFC')
	.pipe(
		codePointString(1, MAX_APPLICATION_COMMAND_NAME_LENGTH).refine(
			(value) => /^[\p{L}\p{N}_-]+$/u.test(value) && value === value.toLowerCase(),
			'Expected lowercase letters, numbers, hyphens or underscores',
		),
	);

// Wire IDs remain strings and share the storage primitive's signed 64-bit snowflake range.
export const ApplicationCommandSnowflake = SnowflakeStringType.max(19).pipe(
	z.string().refine((value) => value !== '0' && BigInt(value) <= 9223372036854775807n, 'Invalid snowflake'),
);
// discord.py writes some IDs as JSON integers; larger ones already arrive as strings from the lossless body parser.
export const ApplicationCommandSnowflakeInput = z.preprocess(
	(value) => (typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? String(value) : value),
	ApplicationCommandSnowflake,
);
export const ApplicationCommandInteger = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
export const ApplicationCommandNumber = z.number().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
export const ApplicationCommandStringValue = codePointString(0, MAX_APPLICATION_COMMAND_STRING_LENGTH);

const Description = codePointString(1, MAX_APPLICATION_COMMAND_DESCRIPTION_LENGTH);
export const ApplicationCommandChoiceName = codePointString(1, MAX_APPLICATION_COMMAND_CHOICE_NAME_LENGTH);
export const ApplicationCommandChoiceStringValue = codePointString(0, MAX_APPLICATION_COMMAND_CHOICE_STRING_LENGTH);
const OptionFields = {
	name: ApplicationCommandName,
	description: Description,
	required: z.boolean().default(false),
};

function choices<T extends z.ZodType>(value: T) {
	return z
		.array(z.strictObject({name: ApplicationCommandChoiceName, value}))
		.max(MAX_APPLICATION_COMMAND_CHOICES)
		.optional();
}

interface OptionBounds {
	choices?: ReadonlyArray<unknown>;
	autocomplete?: boolean;
	min_value?: number;
	max_value?: number;
	min_length?: number;
	max_length?: number;
}

function checkOptionBounds(option: OptionBounds, ctx: z.RefinementCtx) {
	if (option.autocomplete && option.choices?.length)
		ctx.addIssue({code: 'custom', message: 'Autocomplete options cannot have choices'});
	if (option.min_value !== undefined && option.max_value !== undefined && option.min_value > option.max_value)
		ctx.addIssue({code: 'custom', message: 'min_value cannot exceed max_value'});
	if (option.min_length !== undefined && option.max_length !== undefined && option.min_length > option.max_length)
		ctx.addIssue({code: 'custom', message: 'min_length cannot exceed max_length'});
}

const StringLength = z.number().int().min(0).max(MAX_APPLICATION_COMMAND_STRING_LENGTH);

const ValueOption = z.discriminatedUnion('type', [
	z
		.strictObject({
			...OptionFields,
			type: z.literal(ApplicationCommandOptionTypes.STRING),
			choices: choices(ApplicationCommandChoiceStringValue),
			autocomplete: z.boolean().optional(),
			min_length: StringLength.optional(),
			max_length: StringLength.min(1).optional(),
		})
		.superRefine(checkOptionBounds),
	z
		.strictObject({
			...OptionFields,
			type: z.literal(ApplicationCommandOptionTypes.INTEGER),
			choices: choices(ApplicationCommandInteger),
			autocomplete: z.boolean().optional(),
			min_value: ApplicationCommandInteger.optional(),
			max_value: ApplicationCommandInteger.optional(),
		})
		.superRefine(checkOptionBounds),
	z.strictObject({...OptionFields, type: z.literal(ApplicationCommandOptionTypes.BOOLEAN)}),
	z.strictObject({...OptionFields, type: z.literal(ApplicationCommandOptionTypes.USER)}),
	z.strictObject({
		...OptionFields,
		type: z.literal(ApplicationCommandOptionTypes.CHANNEL),
		channel_types: z.array(z.number().int().min(0).max(255)).max(25).optional(),
	}),
	z.strictObject({...OptionFields, type: z.literal(ApplicationCommandOptionTypes.ROLE)}),
	z.strictObject({...OptionFields, type: z.literal(ApplicationCommandOptionTypes.MENTIONABLE)}),
	z.strictObject({...OptionFields, type: z.literal(ApplicationCommandOptionTypes.ATTACHMENT)}),
	z
		.strictObject({
			...OptionFields,
			type: z.literal(ApplicationCommandOptionTypes.NUMBER),
			choices: choices(ApplicationCommandNumber),
			autocomplete: z.boolean().optional(),
			min_value: ApplicationCommandNumber.optional(),
			max_value: ApplicationCommandNumber.optional(),
		})
		.superRefine(checkOptionBounds),
]);

function uniqueNames(options: ReadonlyArray<{name: string}>, ctx: z.RefinementCtx) {
	const names = new Set<string>();
	for (const [index, option] of options.entries()) {
		if (names.has(option.name)) ctx.addIssue({code: 'custom', path: [index, 'name'], message: 'Duplicate option name'});
		names.add(option.name);
	}
}

function requiredFirst(options: ReadonlyArray<{required?: boolean}>, ctx: z.RefinementCtx) {
	let optionalSeen = false;
	for (const [index, option] of options.entries()) {
		if (option.required && optionalSeen)
			ctx.addIssue({
				code: 'custom',
				path: [index, 'required'],
				message: 'Required options must precede optional options',
			});
		if (!option.required) optionalSeen = true;
	}
}

const ValueOptions = z
	.array(ValueOption)
	.max(MAX_APPLICATION_COMMAND_OPTIONS)
	.superRefine((options, ctx) => {
		uniqueNames(options, ctx);
		requiredFirst(options, ctx);
	});

const SubCommandOption = z.strictObject({
	type: z.literal(ApplicationCommandOptionTypes.SUB_COMMAND),
	name: ApplicationCommandName,
	description: Description,
	options: ValueOptions.default([]),
});

// Discord allows two levels: a group holds subcommands, a subcommand holds values.
const SubCommandGroupOption = z.strictObject({
	type: z.literal(ApplicationCommandOptionTypes.SUB_COMMAND_GROUP),
	name: ApplicationCommandName,
	description: Description,
	options: z.array(SubCommandOption).min(1).max(MAX_APPLICATION_COMMAND_OPTIONS).superRefine(uniqueNames),
});

export const ApplicationCommandOption = z.discriminatedUnion('type', [
	SubCommandOption,
	SubCommandGroupOption,
	...ValueOption.options,
]);
export type ApplicationCommandOption = z.infer<typeof ApplicationCommandOption>;

const SUBCOMMAND_TYPES = new Set<number>([
	ApplicationCommandOptionTypes.SUB_COMMAND,
	ApplicationCommandOptionTypes.SUB_COMMAND_GROUP,
]);

const Options = z
	.array(ApplicationCommandOption)
	.max(MAX_APPLICATION_COMMAND_OPTIONS)
	.superRefine((options, ctx) => {
		uniqueNames(options, ctx);
		const subcommands = options.filter((option) => SUBCOMMAND_TYPES.has(option.type)).length;
		if (subcommands > 0 && subcommands < options.length)
			ctx.addIssue({code: 'custom', message: 'Subcommands cannot sit beside value options'});
		if (subcommands === 0) requiredFirst(options as ReadonlyArray<{required?: boolean}>, ctx);
	});

const DefaultMemberPermissions = z
	.string()
	.max(20)
	.regex(/^(0|[1-9][0-9]*)$/)
	.pipe(UnsignedInt64StringType)
	.pipe(z.string().refine((value) => (BigInt(value) & ~ALL_PERMISSIONS) === 0n, 'Unsupported permission bits'))
	.nullable();

// Discord allows capitals and spaces in the name of a user or message (context menu) command.
export const ContextMenuCommandName = z
	.string()
	.normalize('NFC')
	.pipe(
		codePointString(1, MAX_APPLICATION_COMMAND_NAME_LENGTH).refine(
			(value) => value.trim().length > 0 && !/\p{Cc}/u.test(value),
			'Expected a visible name',
		),
	);

const ChatInputFields = {
	type: z.literal(ApplicationCommandTypes.CHAT_INPUT),
	name: ApplicationCommandName,
	description: Description,
	options: Options.default([]),
};

// A context menu command has no description and no options; Discord answers it with an empty description.
const ContextMenuFields = {
	type: z.literal([ApplicationCommandTypes.USER, ApplicationCommandTypes.MESSAGE]),
	name: ContextMenuCommandName,
	description: z
		.literal('')
		.nullish()
		.transform((): string => ''),
	options: z
		.array(z.never())
		.max(0)
		.nullish()
		.transform((): Array<ApplicationCommandOption> => []),
};

const SharedCommandFields = {
	default_member_permissions: DefaultMemberPermissions.default(null),
	dm_permission: z.boolean().default(false),
};

// These bounds apply to normalized JSON; controllers must also bound the raw HTTP body before parsing it.
function commandWithinByteLimit(command: unknown): boolean {
	return new TextEncoder().encode(JSON.stringify(command)).byteLength <= MAX_APPLICATION_COMMAND_BYTES;
}

// Reserve worst-case guild metadata even for global commands; appending fields replaces one brace with a comma.
const MAX_COMMAND_SERVER_FIELDS_BYTES =
	new TextEncoder().encode(
		JSON.stringify({
			id: '9223372036854775807',
			application_id: '9223372036854775807',
			version: '9223372036854775807',
			guild_id: '9223372036854775807',
		}),
	).byteLength - 1;

function commandRequestWithinByteLimit(command: unknown): boolean {
	return (
		new TextEncoder().encode(JSON.stringify(command)).byteLength + MAX_COMMAND_SERVER_FIELDS_BYTES <=
		MAX_APPLICATION_COMMAND_BYTES
	);
}

// Discord's interaction contexts: 0 a guild, 1 the bot's DM, 2 a private channel (none here).
const INTERACTION_CONTEXT_GUILD = 0;
const INTERACTION_CONTEXT_BOT_DM = 1;
const InteractionContexts = z.array(z.number().int().min(0).max(2)).max(3);

// User-installed apps do not exist here, so the integration types are dropped. Sent contexts decide the bot DM and
// are kept, so a command without the guild context stays out of guilds.
const DiscordContextFields = {
	contexts: InteractionContexts.nullish(),
	integration_types: z.array(z.number().int()).nullish(),
};

function withContexts<T extends {contexts?: Array<number> | null; integration_types?: unknown; dm_permission: boolean}>(
	command: T,
): Omit<T, 'contexts' | 'integration_types'> & {contexts?: Array<number>} {
	const {contexts, integration_types: _integrationTypes, ...rest} = command;
	if (contexts == null) return rest;
	return {...rest, contexts, dm_permission: contexts.includes(INTERACTION_CONTEXT_BOT_DM)};
}

export function allowsGuildContext(command: {contexts?: ReadonlyArray<number> | null}): boolean {
	return command.contexts == null || command.contexts.includes(INTERACTION_CONTEXT_GUILD);
}

// Sent by discord.py and discord.js and never stored: localized names (the default name shows in every locale for
// now), the old default_permission flag and the entry point handler. A false nsfw goes too; true stays and is refused,
// as no channel gates commands by age yet. JDA says autocomplete: false on every option; it goes where autocomplete
// means nothing, and true there stays and is refused.
const AUTOCOMPLETE_OPTION_TYPES = new Set<unknown>([
	ApplicationCommandOptionTypes.STRING,
	ApplicationCommandOptionTypes.INTEGER,
	ApplicationCommandOptionTypes.NUMBER,
]);

const DISCORD_ONLY_FIELDS = new Set([
	'name_localizations',
	'description_localizations',
	'name_localized',
	'description_localized',
	'default_permission',
	'handler',
]);

function withoutDiscordOnlyFields(value: unknown): unknown {
	if (Array.isArray(value)) return value.map(withoutDiscordOnlyFields);
	if (value === null || typeof value !== 'object') return value;
	const optionType = (value as {type?: unknown}).type;
	return Object.fromEntries(
		Object.entries(value)
			.filter(
				([key, entry]) =>
					!DISCORD_ONLY_FIELDS.has(key) &&
					!(key === 'nsfw' && entry !== true) &&
					!(key === 'autocomplete' && entry === false && !AUTOCOMPLETE_OPTION_TYPES.has(optionType)),
			)
			.map(([key, entry]) => [key, withoutDiscordOnlyFields(entry)]),
	);
}

// discord.py sends default_member_permissions as a number. A command without a type is a slash command.
function fromDiscordLibraries(value: unknown): unknown {
	const stripped = withoutDiscordOnlyFields(value);
	if (stripped === null || typeof stripped !== 'object' || Array.isArray(stripped)) return stripped;
	const command = stripped as Record<string, unknown>;
	const typed = command.type === undefined ? {...command, type: ApplicationCommandTypes.CHAT_INPUT} : command;
	const permissions = command.default_member_permissions;
	const whole =
		typeof permissions === 'bigint' ||
		(typeof permissions === 'number' && Number.isSafeInteger(permissions) && permissions >= 0);
	return whole ? {...typed, default_member_permissions: String(permissions)} : typed;
}

const CommandRequestBody = z.discriminatedUnion('type', [
	z.strictObject({...ChatInputFields, ...SharedCommandFields, ...DiscordContextFields}),
	z.strictObject({...ContextMenuFields, ...SharedCommandFields, ...DiscordContextFields}),
]);

export const ApplicationCommandRequest = z.preprocess(
	fromDiscordLibraries,
	CommandRequestBody.transform(withContexts).refine(commandRequestWithinByteLimit, 'Command exceeds UTF-8 byte limit'),
);
export type ApplicationCommandRequest = z.infer<typeof ApplicationCommandRequest>;

// Discord ignores dm_permission on a guild command and discord.py sends true by default, so it is stored false.
export const GuildApplicationCommandRequest = z.preprocess(
	fromDiscordLibraries,
	CommandRequestBody.transform((command) => ({...withContexts(command), dm_permission: false as const})).refine(
		commandRequestWithinByteLimit,
		'Command exceeds UTF-8 byte limit',
	),
);
export type GuildApplicationCommandRequest = z.infer<typeof GuildApplicationCommandRequest>;

// Names are unique per command type, as on Discord: a slash command and a user command may share one.
function commandSet<T extends z.ZodType<{name: string; type: number}>>(command: T) {
	return z
		.array(command)
		.max(MAX_APPLICATION_COMMANDS_PER_SCOPE)
		.refine(
			(commands) => new Set(commands.map(({type, name}) => `${type}:${name}`)).size === commands.length,
			'Duplicate command name',
		)
		.refine(
			(commands) =>
				new TextEncoder().encode(JSON.stringify(commands)).byteLength +
					commands.length * MAX_COMMAND_SERVER_FIELDS_BYTES <=
				MAX_APPLICATION_COMMAND_SET_BYTES,
			'Command set exceeds UTF-8 byte limit',
		);
}

export const ApplicationCommandBulkOverwriteRequest = commandSet(ApplicationCommandRequest);
export type ApplicationCommandBulkOverwriteRequest = z.infer<typeof ApplicationCommandBulkOverwriteRequest>;
export const GuildApplicationCommandBulkOverwriteRequest = commandSet(GuildApplicationCommandRequest);
export type GuildApplicationCommandBulkOverwriteRequest = z.infer<typeof GuildApplicationCommandBulkOverwriteRequest>;

export const ApplicationCommandResponse = z
	.strictObject({
		type: z
			.literal([ApplicationCommandTypes.CHAT_INPUT, ApplicationCommandTypes.USER, ApplicationCommandTypes.MESSAGE])
			.default(ApplicationCommandTypes.CHAT_INPUT),
		name: z.union([ApplicationCommandName, ContextMenuCommandName]),
		description: z.union([Description, z.literal('')]),
		options: Options.default([]),
		...SharedCommandFields,
		id: ApplicationCommandSnowflake,
		application_id: ApplicationCommandSnowflake,
		version: ApplicationCommandSnowflake,
		guild_id: ApplicationCommandSnowflake.optional(),
		contexts: InteractionContexts.optional(),
	})
	.refine((command) => command.guild_id === undefined || !command.dm_permission, 'Guild commands cannot permit DM use')
	.refine(commandWithinByteLimit, 'Command exceeds UTF-8 byte limit');
export type ApplicationCommandResponse = z.infer<typeof ApplicationCommandResponse>;

export const ApplicationCommandsUpdateEvent = z.strictObject({
	application_id: ApplicationCommandSnowflake,
	scope_id: ApplicationCommandSnowflake,
	version: ApplicationCommandSnowflake,
});
export type ApplicationCommandsUpdateEvent = z.infer<typeof ApplicationCommandsUpdateEvent>;

export const ApplicationCommandListResponse = z.array(ApplicationCommandResponse);
export type ApplicationCommandListResponse = z.infer<typeof ApplicationCommandListResponse>;

// Merged into the stored command and validated as a whole.
export const ApplicationCommandPatchRequest = z.record(z.string(), z.unknown());
export type ApplicationCommandPatchRequest = z.infer<typeof ApplicationCommandPatchRequest>;

export const ApplicationCommandsParam = z.object({
	application_id: SnowflakeType.describe('The ID of the application'),
});
export const ApplicationCommandIdParam = ApplicationCommandsParam.extend({
	command_id: SnowflakeType.describe('The ID of the command'),
});
export const GuildApplicationCommandsParam = ApplicationCommandsParam.extend({
	guild_id: SnowflakeType.describe('The ID of the guild'),
});
export const GuildApplicationCommandIdParam = GuildApplicationCommandsParam.extend({
	command_id: SnowflakeType.describe('The ID of the command'),
});
